/**
 * About Page V2 — Interactive JS
 * Handles: Plant Plug Difference tabs, Crew carousel
 * ⑥ One diff card visible at a time (all viewports)
 * ⑦ Crew section: scroll-snap carousel on desktop, hide/show on mobile
 * Keyboard accessible, reduced-motion safe.
 */

class AboutPageV2 {
  constructor(section) {
    this.section = section;
    this.prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

    this.initDiffTabs();
    this.initCrewTabs();
    this.initCrewHeight();
    this.initHeroScrollCta();
    this.initCollageStack();
  }

  // ─── Crew card height — match tallest content ────────────────────────────
  // James asked: cards wrap to content + all cards share the height of the one
  // with the most content. JS measures every crew card's content at the active
  // card's content width, finds the max, sets a CSS variable. CSS gives every
  // card min-height: var(--apv2-crew-row-h) so align-items:stretch on the row
  // settles every card to that maximum.

  initCrewHeight() {
    const wrap = this.section.querySelector('.apv2-crew-cards');
    const contents = this.section.querySelectorAll('.apv2-crew-card-content');
    if (!wrap || !contents.length) return;

    const measure = () => {
      if (!this.isDesktop()) {
        wrap.style.removeProperty('--apv2-crew-row-h');
        return;
      }
      const activeContent = this.section.querySelector(
        '.apv2-crew-card.is-active .apv2-crew-card-content'
      );
      const probeWidth = activeContent && activeContent.offsetWidth > 0
        ? activeContent.offsetWidth
        : 300;
      const probe = document.createElement('div');
      probe.className = 'apv2-crew-card-content';
      // !important needed to override .apv2-crew-card-content's desktop rule
      // (position:absolute; inset:0 0 0 220px) — without it probe would inherit
      // inset:0 and stretch to viewport height → 10000+px measurement.
      probe.style.cssText =
        'position:fixed !important;left:-9999px !important;top:0 !important;' +
        'right:auto !important;bottom:auto !important;inset:auto !important;' +
        'visibility:hidden !important;opacity:0 !important;pointer-events:none !important;' +
        'box-sizing:border-box !important;padding:36px !important;' +
        'width:' + probeWidth + 'px !important;' +
        'height:auto !important;max-height:none !important;min-height:0 !important;' +
        'display:flex !important;flex-direction:column !important;gap:16px !important;';
      document.body.appendChild(probe);
      let maxH = 0;
      contents.forEach((c) => {
        probe.innerHTML = c.innerHTML;
        const h = probe.offsetHeight;
        if (h > maxH) maxH = h;
      });
      document.body.removeChild(probe);
      if (maxH > 0) {
        wrap.style.setProperty('--apv2-crew-row-h', maxH + 'px');
      }
    };

    // Run after layout settles (active card class already set by initCrewTabs)
    requestAnimationFrame(() => requestAnimationFrame(measure));

    // Re-measure on viewport resize (column widths change)
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 200);
    });
  }

  // ─── Hero scroll CTA ──────────────────────────────────────────────────────

  initHeroScrollCta() {
    const cta = this.section.querySelector('.apv2-hero__cta');
    const target = this.section.querySelector('#apv2-origin');
    if (!cta || !target) return;

    cta.addEventListener('click', (e) => {
      e.preventDefault();
      target.scrollIntoView({
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  // ─── Collage scroll-triggered fly-in (Motion One) ─────────────────────────
  // rev6.4: scroll is the TRIGGER, not the scrub. Each polaroid has its own
  // trigger point (further down the section than the previous one). When the
  // trigger hits, that polaroid plays a fixed-duration fly-in animation to
  // completion — independent of further scrolling.
  //
  // We watch the collage's TOP edge against viewport-percentage thresholds.
  // IntersectionObserver can't express "trigger when top edge passes line N"
  // for a tall element (intersection fires on any overlap), so we use a
  // throttled scroll listener that reads getBoundingClientRect each frame.

  initCollageStack() {
    const collage = this.section.querySelector('.apv2-collage');
    if (!collage) return;

    const imgs = Array.from(collage.querySelectorAll('.apv2-collage-img'));
    if (!imgs.length) return;

    if (this.prefersReducedMotion) return;
    if (typeof window.Motion === 'undefined' || !window.Motion.animate) return;

    const { animate } = window.Motion;

    // Per-polaroid fly-in pose: cards flung from alternating sides, off-screen,
    // strong rotation. Settles toward the resting scatter via easeOutBack.
    const flyIn = [
      { x: -480, y: -380, rotate: -28 },
      { x:  500, y: -340, rotate:  32 },
      { x: -440, y: -300, rotate: -24 },
      { x:  460, y: -380, rotate:  28 },
    ];

    // Resting positions match the CSS .apv2-collage-img--N rules so the
    // reduced-motion fallback looks like the animated end state.
    const rest = [
      { x:  20, y: 0, rotate: -3 },
      { x: -16, y: 0, rotate:  3 },
      { x:  16, y: 0, rotate: -2 },
      { x: -20, y: 0, rotate:  3 },
    ];

    // Viewport-position threshold per polaroid (as % of viewport height from
    // top). Each fires when the collage's TOP edge crosses that line on the
    // way up (user scrolling down). Earlier-firing polaroids have HIGHER %
    // (closer to viewport bottom = collage just entered).
    //
    //   75% → fires when collage top edge is 75% down viewport (just entered)
    //   30% → fires when collage top has scrolled ~70% past viewport top
    const thresholdsPct = [75, 60, 45, 30];

    // Per-polaroid animation: time-based, runs to completion once triggered.
    const duration = 0.75;
    const easing = [0.34, 1.56, 0.64, 1]; // easeOutBack — slight overshoot

    // Pre-set initial pose so polaroids aren't visible at rest before triggers.
    imgs.forEach((img, i) => {
      const f = flyIn[i] || flyIn[flyIn.length - 1];
      img.style.opacity = '0';
      img.style.transform = `translate(${f.x}px, ${f.y}px) rotate(${f.rotate}deg)`;
    });

    const fired = imgs.map(() => false);

    const playOne = (i) => {
      if (fired[i]) return;
      fired[i] = true;
      const img = imgs[i];
      const f = flyIn[i] || flyIn[flyIn.length - 1];
      const r = rest[i] || rest[rest.length - 1];
      animate(
        img,
        {
          x: [f.x, r.x],
          y: [f.y, r.y],
          rotate: [f.rotate, r.rotate],
          opacity: [0, 1],
        },
        { duration, easing }
      );
    };

    const check = () => {
      const top = collage.getBoundingClientRect().top;
      const vh = window.innerHeight || 1;
      const pct = (top / vh) * 100; // 100 = at bottom of viewport, 0 = at top
      thresholdsPct.forEach((t, i) => {
        if (pct <= t) playOne(i);
      });
      if (fired.every(Boolean)) {
        window.removeEventListener('scroll', onScroll, { passive: true });
      }
    };

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; check(); });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    check(); // run once at init (covers reloads scrolled mid-page)
  }

  // ─── Plant Plug Difference tabs ───────────────────────────────────────────

  // Plant Plug Difference — normal-flow tabs + sticky intro.
  // Cards are stacked in the right column; left intro sticks via CSS.
  // Tab click scrolls the matching card into view. IntersectionObserver
  // updates the active tab as the user scrolls past each card.

  initDiffTabs() {
    this.diffCardsWrap = this.section.querySelector('.apv2-diff-cards');
    this.diffTabs = this.section.querySelectorAll('.apv2-diff-tab');
    this.diffCardsList = this.section.querySelectorAll('.apv2-diff-card');
    this.lastDiffIdx = -1;

    if (!this.diffTabs.length || !this.diffCardsList.length) return;

    this.diffTabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.handleDiffTabClick(i));
      tab.addEventListener('keydown', (e) => {
        let newIdx = null;
        const n = this.diffTabs.length;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') newIdx = (i + 1) % n;
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') newIdx = (i - 1 + n) % n;
        else if (e.key === 'Home') newIdx = 0;
        else if (e.key === 'End') newIdx = n - 1;
        if (newIdx !== null) {
          e.preventDefault();
          this.diffTabs[newIdx].focus();
          this.handleDiffTabClick(newIdx);
        }
      });
    });

    this.activateDiffTab(0, this.diffTabs, this.diffCardsList);

    // IntersectionObserver: update active tab as cards enter the viewport.
    // rootMargin pushes the trigger line to the upper-middle of the viewport
    // so the tab updates as soon as the card becomes the dominant visible one.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        // Pick the most-intersecting card that just became visible.
        let best = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          }
        });
        if (best) {
          const idx = Array.from(this.diffCardsList).indexOf(best.target);
          if (idx !== -1 && idx !== this.lastDiffIdx) {
            this.lastDiffIdx = idx;
            this.activateDiffTab(idx, this.diffTabs, this.diffCardsList);
          }
        }
      }, {
        rootMargin: '-20% 0px -40% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });

      this.diffCardsList.forEach((card) => io.observe(card));
    }
  }

  handleDiffTabClick(i) {
    // Activate the tab immediately, then scroll the matching card into view.
    this.activateDiffTab(i, this.diffTabs, this.diffCardsList);
    const card = this.diffCardsList[i];
    if (card) {
      card.scrollIntoView({
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }

  activateDiffTab(activeIdx, tabs, cards) {
    tabs.forEach((tab, i) => {
      const isActive = i === activeIdx;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // All cards visible in normal flow — just toggle is-active highlight.
    cards.forEach((card, i) => {
      card.classList.toggle('is-active', i === activeIdx);
    });

    // Auto-scroll the tablist so the active tab stays in view (mobile rail
    // is horizontal-scrollable; if the active tab is off-screen the user
    // can't see which card is current).
    this.scrollTabIntoView(this.section.querySelector('.apv2-diff-tablist'), tabs[activeIdx]);
  }

  // Scroll an internal tablist (overflow-x:auto on mobile, column on desktop)
  // so the given tab is centered. Manual scrollTo avoids scrollIntoView's
  // side-effect of scrolling all ancestors (which would jump the page).
  scrollTabIntoView(tablist, tab) {
    if (!tablist || !tab) return;
    const isHorizontal = tablist.scrollWidth > tablist.clientWidth;
    if (isHorizontal) {
      const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
      const target = Math.max(0, tabCenter - tablist.clientWidth / 2);
      tablist.scrollTo({ left: target, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
    } else if (tablist.scrollHeight > tablist.clientHeight) {
      const tabCenter = tab.offsetTop + tab.offsetHeight / 2;
      const target = Math.max(0, tabCenter - tablist.clientHeight / 2);
      tablist.scrollTo({ top: target, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
    }
  }

  // ─── Crew carousel ────────────────────────────────────────────────────────

  initCrewTabs() {
    const tabs = this.section.querySelectorAll('.apv2-crew-tab');
    const cards = this.section.querySelectorAll('.apv2-crew-card');
    this.crewCardsWrap = this.section.querySelector('.apv2-crew-cards');

    if (!tabs.length || !cards.length) return;

    // ⑦ Desktop: unhide all cards so scroll-snap carousel can show them
    if (this.isDesktop() && this.crewCardsWrap) {
      cards.forEach((card) => {
        card.removeAttribute('hidden');
        card.setAttribute('aria-hidden', 'true');
      });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () =>
        this.activateCrewTab(i, tabs, cards)
      );

      tab.addEventListener('keydown', (e) => {
        let newIdx = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          newIdx = (i + 1) % tabs.length;
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          newIdx = (i - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          newIdx = 0;
        } else if (e.key === 'End') {
          newIdx = tabs.length - 1;
        }
        if (newIdx !== null) {
          e.preventDefault();
          tabs[newIdx].focus();
          this.activateCrewTab(newIdx, tabs, cards);
        }
      });
    });

    // rev6.5: clicking an inactive card promotes it to active. Skip the
    // active card itself (its content has interactive elements like links).
    cards.forEach((card, i) => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('is-active')) return;
        // Don't hijack clicks on interactive descendants inside the active card
        if (e.target.closest('a, button, input, select, textarea')) return;
        this.activateCrewTab(i, tabs, cards);
      });
    });

    // Initial state
    this.activateCrewTab(0, tabs, cards);
  }

  activateCrewTab(activeIdx, tabs, cards) {
    tabs.forEach((tab, i) => {
      const isActive = i === activeIdx;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Keep the active crew tab in view inside the (possibly overflowing) tablist
    this.scrollTabIntoView(this.section.querySelector('.apv2-crew-tablist'), tabs[activeIdx]);

    if (this.isDesktop() && this.crewCardsWrap) {
      // ⑤ Annotation: FLIP reorder so active card is always leftmost,
      // previous-active rotates to the right end (infinite loop), every
      // card animates from old → new position smoothly.
      const cardArr = Array.from(cards);

      // 1. FIRST — capture current rects before any DOM change
      const firstRects = new Map();
      cardArr.forEach((card) => {
        firstRects.set(card, card.getBoundingClientRect());
      });

      // 2. Update active/aria state on the *card references* (independent of DOM order)
      cardArr.forEach((card, i) => {
        const isActive = i === activeIdx;
        card.removeAttribute('hidden');
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        // Clear any leftover inline transform from a previous FLIP cycle
        card.style.transition = 'none';
        card.style.transform = '';
      });

      // 3. LAST — reorder DOM: active first, then circular sequence
      const reordered = [];
      for (let i = 0; i < cardArr.length; i++) {
        reordered.push(cardArr[(activeIdx + i) % cardArr.length]);
      }
      reordered.forEach((card) => this.crewCardsWrap.appendChild(card));

      // 4. INVERT + PLAY — compute deltas, then animate to natural pos via Motion One
      const motion = window.Motion;
      if (!motion || !motion.animate || this.prefersReducedMotion) {
        // Fallback: snap to new positions, no animation
        reordered.forEach((card) => { card.style.transform = ''; });
        return;
      }

      reordered.forEach((card) => {
        const oldRect = firstRects.get(card);
        const newRect = card.getBoundingClientRect();
        const dx = oldRect.left - newRect.left;
        const dy = oldRect.top - newRect.top;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          motion.animate(
            card,
            { x: [dx, 0], y: [dy, 0] },
            { duration: 0.55, easing: [0.2, 0.8, 0.2, 1] }
          );
        }
      });
    } else {
      // rev6.5 — mobile: cards row is a horizontal scroll-snap carousel. All
      // cards remain visible; tab click scrolls the matching card into view.
      cards.forEach((card, i) => {
        const isActive = i === activeIdx;
        card.removeAttribute('hidden');
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        card.style.transform = '';
        card.style.transition = '';
      });
      if (this.crewCardsWrap && cards[activeIdx]) {
        const target = cards[activeIdx];
        const wrap = this.crewCardsWrap;
        const targetLeft = target.offsetLeft - (wrap.clientWidth - target.offsetWidth) / 2;
        wrap.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
        });
      }
    }
  }

}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

function initAboutPageV2() {
  document
    .querySelectorAll('[data-section-type="about-page-v2"]')
    .forEach((el) => new AboutPageV2(el));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAboutPageV2);
} else {
  initAboutPageV2();
}

// Shopify theme editor re-init
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.dataset.sectionType === 'about-page-v2') {
      new AboutPageV2(event.target);
    }
  });
}
